import { supabase } from "./supabase";

/**
 * Service for fetching payout dashboard data for admin view
 * Shows all affiliates with Wise registration status, balances, and payout history
 */
const payoutDashboardService = {
  /**
   * Fetch all payout dashboard data using the RPC function
   * Returns aggregated data for all affiliates including:
   * - Wise registration status
   * - Unpaid balance
   * - Last payout date
   * - Total paid out
   */
  async getPayoutDashboardData() {
    try {
      console.log("📊 Fetching payout dashboard data...");

      const { data, error } = await supabase.rpc("get_payout_dashboard_data");

      if (error) {
        console.error("❌ Error fetching payout dashboard data:", error);
        return { success: false, error: error.message, data: [] };
      }

      console.log(`✅ Fetched ${data?.length || 0} affiliate records`);
      return { success: true, data: data || [] };
    } catch (err) {
      console.error("❌ Error in getPayoutDashboardData:", err);
      return { success: false, error: err.message, data: [] };
    }
  },

  /**
   * Calculate summary statistics from the dashboard data
   * @param {Array} data - Array of affiliate payout data
   * @returns {Object} Summary statistics
   */
  getSummaryStats(data) {
    if (!data || !Array.isArray(data)) {
      return {
        registered: 0,
        notRegistered: 0,
        totalOwed: 0,
        totalPaidOut: 0,
        totalAffiliates: 0,
      };
    }

    const registered = data.filter((d) => d.has_wise_account).length;
    const notRegistered = data.filter((d) => !d.has_wise_account).length;
    const totalOwed = data.reduce(
      (sum, d) => sum + parseFloat(d.unpaid_balance || 0),
      0
    );
    const totalPaidOut = data.reduce(
      (sum, d) => sum + parseFloat(d.total_paid_out || 0),
      0
    );

    return {
      registered,
      notRegistered,
      totalOwed,
      totalPaidOut,
      totalAffiliates: data.length,
    };
  },

  /**
   * Fetch week finalization status for the dashboard
   * Shows which weeks are fully finalized and if they've been auto-processed
   */
  async getWeekFinalizationStatus(weeksBack = 6) {
    try {
      console.log("📅 Fetching week finalization status...");

      const { data, error } = await supabase.rpc("get_week_finalization_status", {
        p_weeks_back: weeksBack,
      });

      if (error) {
        console.error("❌ Error fetching week status:", error);
        return { success: false, error: error.message, data: [] };
      }

      console.log(`✅ Fetched ${data?.length || 0} weeks of status`);
      return { success: true, data: data || [] };
    } catch (err) {
      console.error("❌ Error in getWeekFinalizationStatus:", err);
      return { success: false, error: err.message, data: [] };
    }
  },

  /**
   * Fetch auto-payout run history
   */
  async getAutoPayoutHistory() {
    try {
      console.log("🤖 Fetching auto-payout history...");

      const { data, error } = await supabase
        .from("automatic_payout_runs")
        .select("*")
        .order("week_start", { ascending: false })
        .limit(10);

      if (error) {
        console.error("❌ Error fetching auto-payout history:", error);
        return { success: false, error: error.message, data: [] };
      }

      console.log(`✅ Fetched ${data?.length || 0} auto-payout records`);
      return { success: true, data: data || [] };
    } catch (err) {
      console.error("❌ Error in getAutoPayoutHistory:", err);
      return { success: false, error: err.message, data: [] };
    }
  },

  /**
   * Get affiliates eligible for automatic payout (Wise + $25+ balance)
   */
  async getEligibleAffiliates() {
    try {
      const { data, error } = await supabase.rpc("get_affiliates_eligible_for_payout");

      if (error) {
        console.error("❌ Error fetching eligible affiliates:", error);
        return { success: false, error: error.message, data: [] };
      }

      return { success: true, data: data || [] };
    } catch (err) {
      console.error("❌ Error in getEligibleAffiliates:", err);
      return { success: false, error: err.message, data: [] };
    }
  },

  /**
   * Fetch failed payout transactions with error details
   * Shows payouts that need attention or are being retried
   */
  async getFailedPayouts() {
    try {
      console.log("❌ Fetching failed payouts...");

      const { data, error } = await supabase
        .from("payout_transaction")
        .select("*")
        .eq("status", "failed")
        .order("reviewed_at", { ascending: false })
        .limit(20);

      if (error) {
        console.error("❌ Error fetching failed payouts:", error);
        return { success: false, error: error.message, data: [] };
      }

      console.log(`✅ Fetched ${data?.length || 0} failed payouts`);
      return { success: true, data: data || [] };
    } catch (err) {
      console.error("❌ Error in getFailedPayouts:", err);
      return { success: false, error: err.message, data: [] };
    }
  },
};

export default payoutDashboardService;
